'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { ExternalLink, Star, X, Megaphone } from 'lucide-react';
import { useAdManagement } from '@/hooks/useAdManagement';
import { AdSpot } from '@/types/ad-management';

type AdVariant = 'banner' | 'sponsored-row' | 'sidebar';

interface MarketHubAdSpotProps {
  /** Which ad placement to render */
  variant: AdVariant;
  /** Additional class names */
  className?: string;
  /** Callback when ad is clicked */
  onSelect?: () => void;
  /** Show close button (for dismissible ads) */
  showCloseButton?: boolean;
  /** Callback when close button clicked */
  onClose?: () => void;
}

/**
 * MarketHubAdSpot - Unified ad component for the Market Hub page
 * Supports three variants:
 * - banner: Wide banner above the asset directory
 * - sponsored-row: Inline sponsored asset row in the table
 * - sidebar: Card-style ad in the sidebar
 */
export default function MarketHubAdSpot({ 
  variant,
  className = '', 
  onSelect,
  showCloseButton = false,
  onClose
}: MarketHubAdSpotProps) {
  const { getActiveAdsByLocation, trackImpression, trackClick, loading } = useAdManagement();
  const [isVisible, setIsVisible] = useState(true);

  // Map variant to location ID
  const locationId = useMemo(() => {
    switch (variant) {
      case 'banner': return 'market-hub-banner';
      case 'sponsored-row': return 'market-hub-sponsored';
      case 'sidebar': return 'market-hub-sidebar';
      default: return 'market-hub-banner';
    }
  }, [variant]);

  // Get the highest priority active ad for this location
  const activeAd: AdSpot | null = useMemo(() => {
    const ads = getActiveAdsByLocation(locationId);
    return ads.length > 0 ? ads[0] : null;
  }, [getActiveAdsByLocation, locationId]);

  // Track impression when ad becomes visible
  useEffect(() => {
    if (activeAd && isVisible) {
      trackImpression(activeAd.id);
    }
  }, [activeAd, isVisible, trackImpression]);

  const handleClick = async () => {
    if (!activeAd) return;

    await trackClick(activeAd.id);

    if (activeAd.buttonUrl) {
      if (activeAd.buttonUrl.startsWith('mailto:')) {
        window.location.href = activeAd.buttonUrl;
      } else {
        window.open(activeAd.buttonUrl, '_blank', 'noopener,noreferrer');
      }
    }
    
    onSelect?.();
  };

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  // Don't render if loading, no active ad, or not visible
  if (loading || !activeAd || !isVisible) return null;

  // Render based on variant
  switch (variant) {
    case 'banner':
      return (
        <BannerAd 
          ad={activeAd} 
          onClick={handleClick} 
          onClose={showCloseButton ? handleClose : undefined}
          className={className}
        />
      );
    case 'sponsored-row':
      return (
        <SponsoredRowAd 
          ad={activeAd} 
          onClick={handleClick}
          className={className}
        />
      );
    case 'sidebar':
      return (
        <SidebarAd 
          ad={activeAd} 
          onClick={handleClick}
          onClose={showCloseButton ? handleClose : undefined}
          className={className}
        />
      );
    default:
      return null;
  }
}

// ============================================
// Banner Ad - Wide banner above asset directory
// ============================================
interface BannerAdProps {
  ad: AdSpot;
  onClick: () => void;
  onClose?: () => void;
  className?: string;
}

function BannerAd({ ad, onClick, onClose, className = '' }: BannerAdProps) {
  return (
    <div 
      className={`relative group bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${className}`}
      onClick={onClick}
    >
      {/* Background image if provided */}
      {ad.imageUrl && (
        <div className="absolute inset-0">
          <img 
            src={ad.imageUrl} 
            alt="" 
            className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity"
          />
        </div>
      )}
      
      {/* Content */}
      <div className="relative flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4">
          {/* Sponsored Badge */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium">
            <Star className="w-3 h-3 fill-current" />
            Sponsored
          </div>
          
          <div>
            <h3 className="text-white font-bold text-lg">{ad.title}</h3>
            <p className="text-white/80 text-sm">{ad.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors"
          >
            {ad.buttonText}
            <ExternalLink size={14} />
          </button>
          
          {/* Close button */}
          {onClose && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              title="Dismiss"
            >
              <X size={14} className="text-white" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Sponsored Row Ad - Inline in asset table
// ============================================
interface SponsoredRowAdProps {
  ad: AdSpot;
  onClick: () => void;
  className?: string;
}

function SponsoredRowAd({ ad, onClick, className = '' }: SponsoredRowAdProps) {
  return (
    <tr 
      className={`bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10 border-l-4 border-amber-500 hover:from-amber-50 hover:to-orange-50 dark:hover:from-amber-900/20 dark:hover:to-orange-900/20 transition-colors cursor-pointer ${className}`}
      onClick={onClick}
    >
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 flex-shrink-0">
            {ad.imageUrl ? (
              <img
                src={ad.imageUrl}
                alt={ad.title}
                className="w-full h-full rounded-lg object-cover"
              />
            ) : (
              <div 
                className="w-full h-full rounded-lg flex items-center justify-center"
                style={{ backgroundColor: ad.backgroundColor }}
              >
                <Megaphone size={18} className="text-white" />
              </div>
            )}
            {/* Sponsored badge */}
            <div className="absolute -top-1 -right-1">
              <div className="bg-amber-500 text-white p-0.5 rounded-full">
                <Star className="w-2.5 h-2.5 fill-current" />
              </div>
            </div>
          </div>
          
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-gray-900 dark:text-white truncate">{ad.title}</span>
              <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-semibold rounded">
                SPONSORED
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{ad.description}</div>
          </div>
        </div>
      </td>
      
      {/* Empty cells to match table structure */}
      <td className="px-4 py-3.5 text-center">
        <span className="text-gray-400">—</span>
      </td>
      <td className="px-4 py-3.5 text-right">
        <span className="text-amber-600 dark:text-amber-400 font-medium text-xs">Featured</span>
      </td>
      <td className="px-4 py-3.5 text-right">
        <span className="text-gray-400">—</span>
      </td>
      <td className="px-4 py-3.5 text-right">
        <span className="text-gray-400">—</span>
      </td>
      <td className="px-4 py-3.5 text-right">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
        >
          {ad.buttonText}
          <ExternalLink size={12} />
        </button>
      </td>
    </tr>
  );
}

// ============================================
// Sidebar Ad - Card in sidebar
// ============================================
interface SidebarAdProps {
  ad: AdSpot;
  onClick: () => void;
  onClose?: () => void;
  className?: string;
}

function SidebarAd({ ad, onClick, onClose, className = '' }: SidebarAdProps) {
  return (
    <div 
      className={`relative group bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl overflow-hidden cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 ${className}`}
      onClick={onClick}
    >
      {/* Close button */}
      {onClose && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
          title="Dismiss"
        >
          <X size={12} className="text-white" />
        </button>
      )}
      
      {/* Image */}
      {ad.imageUrl ? (
        <div className="aspect-[6/5] relative overflow-hidden">
          <img
            src={ad.imageUrl}
            alt={ad.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {/* Sponsored overlay */}
          <div className="absolute top-2 left-2">
            <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full">
              <Star className="w-3 h-3 fill-current" />
              Sponsored
            </div>
          </div>
        </div>
      ) : (
        <div 
          className="aspect-[6/5] flex items-center justify-center"
          style={{ backgroundColor: ad.backgroundColor }}
        >
          <div className="text-center px-4">
            <div className="flex items-center justify-center gap-1 mb-2">
              <Star className="w-5 h-5 text-amber-400 fill-current" />
              <span className="text-white/80 text-xs font-medium">Sponsored</span>
            </div>
            <Megaphone className="w-12 h-12 text-white/80 mx-auto" />
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-1">
          {ad.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
          {ad.description}
        </p>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors"
        >
          {ad.buttonText}
          <ExternalLink size={14} />
        </button>
      </div>
    </div>
  );
}

